import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiClient } from "../../api/apiClient";
import { fetchEstatesByIds } from "../estate/estateList/estateListSlice";
import { setCurrentEstateByIdThunk } from "../estate/currentEstate/currentEstateSlice";
import { clearState } from "../../localStorage";
import { openNotification } from "../uiState";
import { hasEntries } from "../../functions/array";
import { accountActions } from "../accounts/accountSlice";

// Slice
const userSlice = createSlice({
  name: "currentUser",
  initialState: {},
  reducers: {
    clearUser: () => {
      return {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
      if (action.payload) {
        return action.payload;
      }
    });
  },
});

export const { clearUser } = userSlice.actions;

// Async thunks
export const fetchCurrentUserThunk = createAsyncThunk(
  "currentUser/fetchCurrentUser",
  async (_, { dispatch, getState, rejectWithValue }) => {
    const token = getState().sessionToken;

    const sessionResponse = await apiClient().get(`sessions/${token}`);
    if (sessionResponse.status === 200) {
      const { data: sessionData } = sessionResponse;
      const accountId = sessionData.user.accountId;
      dispatch(accountActions.setData(sessionData.user));

      // get user data
      const accountResponse = await apiClient().get(`accounts/${accountId}`);

      if (accountResponse.status === 200) {
        const { person } = accountResponse.data;
        const personResponse = await apiClient().get(
          `persons/${accountResponse.data.person.id}`,
        );

        const { data } = personResponse;

        // ! hack, normally we should get all information from person endpoint, but this is currently not the case
        // ! relations property is missing in person response
        // ! service type ids is missing in account response
        // ! I need to merge booth data to get the needed data
        const userData = {
          ...data,
          ...person,
        };

        if (userData != null && !userData.hasOwnProperty("relations")) {
          // You cannot use the app without additional user data.
          console.warn("Missing relation in user object.", userData);
          return rejectWithValue({
            messageId: "ErrorMessage.missingUserRelations",
            userData,
          });
        }

        if (userData != null && hasEntries(userData.relations)) {
          // set all optional estates for the current user
          const estateIds = userData.relations.map(
            (relation) => relation.estateId,
          );

          dispatch(fetchEstatesByIds([...new Set(estateIds)]));

          try {
            const { currentEstate } = getState().estates;
            // if an estate is already set, this should be skipped
            if (Object.keys(currentEstate).length === 0) {
              dispatch(setCurrentEstateByIdThunk(estateIds[0]));
            }
          } catch (err) {
            console.error(
              "Error occurs while getting current estate state.",
              err,
            );
          }

          return userData;
        }
      }
    }
  },
);

export const logoutUser = createAsyncThunk(
  "currentUser/logout",
  async (_, { dispatch }) => {
    try {
      const response = await apiClient().delete("/sessions/_self");
      if (response.status === 200) {
        dispatch(openNotification("Logged out successfully."));
      } else {
        dispatch(
          openNotification("Error logging out, please contact your admin."),
        );
        console.warn("Error logging out, please contact your admin.", response);
      }
      return true;
    } catch (err) {
      console.error("Problems while fetching account or session data.", err);
      return false;
    } finally {
      dispatch(clearUser());
      clearState();
    }
  },
);

export const reducer = userSlice.reducer;

// selectors
export const selectCurrentUser = (state) => state.currentUser;
